import { useZnBrowserHistory } from '@/Router';
import type { routeTree } from '@/routeTree.gen';
import {
  type LinkComponent,
  type NavigateOptions,
  type RegisteredRouter,
  type RouteIds,
  type ToOptions,
  Link as TsrLink,
  useRouter as useTsRouter,
  useNavigate as useTsrNavigate,
} from '@tanstack/react-router';
import { useCallback } from 'react';

/**
 * This hook is used to update tsr search params.
 * Reuse the same function to update the search params.
 */
export const useUpdateSearchParam = () => {
  const navigate = useTsrNavigate();

  const fn = useCallback(
    (key: string, value: unknown) => {
      navigate({
        // @ts-ignore
        search: (prev) => {
          if (typeof value === 'undefined' || value === null || value === '') {
            if (key in prev) {
              // @ts-ignore
              delete prev[key];
            }

            return prev;
          }

          return {
            ...prev,
            [key]: value,
          };
        },
      });
    },
    [navigate]
  );

  return fn;
};

export const useNavigate = () => {
  const _navigate = useTsrNavigate();
  const { history } = useTsRouter();
  const goBack = useZnBrowserHistory((store) => store.goBack);

  const navigateBack = useCallback(
    <
      TRouter extends RegisteredRouter,
      TTo extends string | undefined,
      TFrom extends string = string,
      TMaskFrom extends string = TFrom,
      TMaskTo extends string = '',
    >(
      to?: NavigateOptions<TRouter, TFrom, TTo, TMaskFrom, TMaskTo>['to'],
      options?: Omit<NavigateOptions<TRouter, TFrom, TTo, TMaskFrom, TMaskTo>, 'to'>
    ) => {
      goBack({
        to,
        ...options,
      } as NavigateOptions);
    },
    [history, goBack, _navigate]
  );

  return { navigateBack };
};

type LinkProps = React.ComponentProps<LinkComponent<'a'>> & {
  isBack?: boolean;
};
export const Link = ({ isBack, ...props }: LinkProps) => {
  const { navigateBack } = useNavigate();

  return (
    <TsrLink
      {...props}
      onClick={(e) => {
        if (isBack) {
          e.preventDefault();
          navigateBack(props.to as ToOptions['to']);
        }
      }}
    />
  );
};

export type RouteTo = ToOptions['to'];
export type RouteId = RouteIds<typeof routeTree>;

export {
  Outlet,
  useParams,
  useRouterState,
} from '@tanstack/react-router';
